import { usePlan } from '../store/planStore.js';
import { INGREDIENTS_BY_ID } from '../../data/index.js';

/**
 * No seeding effect here, deliberately. This screen unmounts every time
 * someone taps another tab, so any component-local "have I seeded yet" flag
 * resets — and an empty staples list is indistinguishable from an untouched
 * one, so it would put back everything a person had just unticked. The store
 * seeds a brand-new plan instead, which is the one moment that genuinely
 * means "a fresh week".
 */
export function Staples({ onDone }: { onDone: () => void }): React.JSX.Element {
  const { ready, plan, library, setStaples } = usePlan();

  if (!ready) return <p>Loading</p>;

  const on = new Set(plan.staples.map((s) => s.ingredientId));

  // Anything in the plan that the library no longer offers still needs a row,
  // or it sits on the list invisible and impossible to remove.
  const orphans = plan.staples
    .filter((s) => !library.staples.some((known) => known.ingredientId === s.ingredientId))
    .map((s) => ({ ingredientId: s.ingredientId, qty: s.qty, unit: s.unit, usuallyOn: false }));
  const rows = [...library.staples, ...orphans];

  const toggle = (ingredientId: string): void => {
    if (on.has(ingredientId)) {
      setStaples(plan.staples.filter((s) => s.ingredientId !== ingredientId));
      return;
    }
    const def = library.staples.find((s) => s.ingredientId === ingredientId);
    if (!def) return;
    setStaples([...plan.staples, { ingredientId: def.ingredientId, qty: def.qty, unit: def.unit }]);
  };

  return (
    <section className="staples">
      <header className="plan-head">
        <h1>Snacks &amp; staples</h1>
        <p className="plan-count">{plan.staples.length} on the list</p>
      </header>

      <ul className="staple-list">
        {rows.map((staple) => {
          const ingredient = INGREDIENTS_BY_ID.get(staple.ingredientId);
          return (
            <li key={staple.ingredientId}>
              <button type="button" aria-pressed={on.has(staple.ingredientId)}
                      onClick={() => toggle(staple.ingredientId)}>
                <span className="tickbox" aria-hidden="true" />
                {ingredient?.name ?? staple.ingredientId}
              </button>
            </li>
          );
        })}
      </ul>

      <button type="button" className="primary" onClick={onDone}>Done</button>
    </section>
  );
}
